import React, { useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowLeft, Clock, Calendar, ChevronRight, Shield } from 'lucide-react';
import Navbar from './Navbar';
import Footer from './Footer';
import SignInModal from './SignInModal';
import PricingModal from './PricingModal';
import PacerModal from './PacerModal';
import Seo from './Seo';
import { getPost, SITE_URL } from '../data/blog';

const CtaBox = ({ onStart, onPricing }) => (
  <div className="my-8 rounded-2xl bg-[#0e0e15] border border-[#c99a2e]/35 gold-glow p-7">
    <h3 className="font-serif-display text-[22px] text-white">Train with a live cadence monitor</h3>
    <p className="mt-2 text-[14px] text-[#9a9aa4] leading-relaxed">
      Steno | PACE MASTER shows courtroom cadence in real time — a live WPM speedometer and subdued audio waves, processed 100% privately in your browser. Test-drive it free for one minute.
    </p>
    <div className="mt-5 flex flex-col sm:flex-row gap-3">
      <button onClick={onStart} className="px-6 py-3 rounded-xl bg-gradient-to-b from-[#e8c766] to-[#d4af37] text-[#1a1500] font-semibold hover:from-[#f0d27a] hover:to-[#e0b84c] transition-all">Start Pacing Free</button>
      <a href={SITE_URL} target="_blank" rel="noopener noreferrer" className="px-6 py-3 rounded-xl bg-[#121218] text-white font-semibold border border-[#2a2a33] hover:border-[#c99a2e]/50 transition-all text-center">Visit stenopacemaster.com</a>
    </div>
  </div>
);

const BlogPost = () => {
  const { slug } = useParams();
  const post = getPost(slug);
  const [signInOpen, setSignInOpen] = useState(false);
  const [pricingOpen, setPricingOpen] = useState(false);
  const [pacerOpen, setPacerOpen] = useState(false);

  if (!post) return <Navigate to="/blog" replace />;

  const url = `${SITE_URL}/blog/${post.slug}`;

  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.metaDescription,
    datePublished: post.date,
    dateModified: post.date,
    author: { '@type': 'Organization', name: post.author, url: SITE_URL },
    publisher: {
      '@type': 'Organization',
      name: 'Steno | PACE MASTER',
      url: SITE_URL,
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    keywords: post.keywords,
    articleSection: post.cities,
    about: post.cities.map((c) => ({ '@type': 'Place', name: c })),
  };

  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: post.faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Blog', item: `${SITE_URL}/blog` },
      { '@type': 'ListItem', position: 3, name: post.title, item: url },
    ],
  };

  return (
    <>
      <Seo
        title={post.metaTitle}
        description={post.metaDescription}
        keywords={post.keywords}
        canonical={url}
        type="article"
        jsonLd={[articleLd, faqLd, breadcrumbLd]}
      />
      <div className="pointer-events-none fixed inset-0 hero-radial" />
      <Navbar onSignIn={() => setSignInOpen(true)} />

      <main className="relative z-10 max-w-3xl mx-auto px-6 pt-28 pb-20">
        <Link to="/blog" className="inline-flex items-center gap-1.5 text-[13px] text-[#8a8a94] hover:text-[#c99a2e] transition-colors mb-8">
          <ArrowLeft className="h-4 w-4" /> Back to blog
        </Link>

        <article className="animate-fade-up">
          <div className="flex flex-wrap gap-2 mb-5">
            {post.cities.map((c) => (
              <span key={c} className="px-2.5 py-1 rounded-md bg-[#141019] border border-[#c99a2e]/20 text-[11px] text-[#c99a2e]">{c}</span>
            ))}
          </div>
          <h1 className="font-serif-display text-[34px] sm:text-[44px] leading-[1.15] text-white">{post.title}</h1>
          <div className="flex items-center gap-4 text-[12.5px] text-[#7c7c86] mt-5 pb-6 border-b border-[#1a1a22]">
            <span className="flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> {new Date(post.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
            <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {post.readTime}</span>
            <span>By {post.author}</span>
          </div>

          {/* AEO / GEO answer block */}
          <div className="mt-7 rounded-xl bg-[#0e0e15] border-l-2 border-[#c99a2e] p-5">
            <div className="text-[11px] tracking-[0.18em] text-[#c99a2e] font-semibold mb-2">QUICK ANSWER</div>
            <p className="text-[15px] leading-relaxed text-[#c9c9d0]">{post.answer}</p>
          </div>

          <div className="mt-8 space-y-1">
            {post.sections.map((s, i) => {
              if (s.type === 'h2') return <h2 key={i} className="font-serif-display text-[27px] text-white mt-10 mb-3">{s.text}</h2>;
              if (s.type === 'h3') return <h3 key={i} className="text-[18px] font-semibold text-[#e0b84c] mt-7 mb-2">{s.text}</h3>;
              return (
                <React.Fragment key={i}>
                  <p className="text-[15.5px] leading-[1.8] text-[#b0b0b8] mb-4">{s.text}</p>
                  {s.cta && <CtaBox onStart={() => setPacerOpen(true)} onPricing={() => setPricingOpen(true)} />}
                </React.Fragment>
              );
            })}
          </div>

          {/* Privacy note */}
          <div className="mt-8 rounded-xl bg-[#0e0e14]/70 border border-[#1e1e26] px-5 py-4 flex items-start gap-3">
            <Shield className="h-4 w-4 mt-0.5 text-[#c99a2e] shrink-0" />
            <p className="text-[12.5px] leading-relaxed text-[#7c7c86]">
              <span className="text-[#c99a2e] font-semibold">100% Private.</span> Steno | PACE MASTER is a real-time visual pace indicator only. No audio, speech, or session data is ever recorded, transmitted, or stored.
            </p>
          </div>

          {/* FAQ */}
          <h2 className="font-serif-display text-[27px] text-white mt-12 mb-4">Frequently Asked Questions</h2>
          <div className="space-y-3">
            {post.faqs.map((f, i) => (
              <div key={i} className="card-border rounded-xl bg-[#0e0e14]/60 p-5">
                <div className="text-[15px] font-semibold text-white mb-2">{f.q}</div>
                <p className="text-[14px] leading-relaxed text-[#9a9aa4]">{f.a}</p>
              </div>
            ))}
          </div>

          {/* Final CTA */}
          <div className="mt-12 text-center rounded-2xl bg-gradient-to-b from-[#12100a] to-[#0e0e14] border border-[#c99a2e]/30 p-9">
            <h2 className="font-serif-display text-[26px] text-white">Ready to break your speed plateau?</h2>
            <p className="mt-3 text-[14px] text-[#9a9aa4] max-w-lg mx-auto">Add real-time cadence monitoring to your daily practice and protect the verbatim record on every job.</p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
              <button onClick={() => setPacerOpen(true)} className="px-7 py-3 rounded-xl bg-gradient-to-b from-[#e8c766] to-[#d4af37] text-[#1a1500] font-semibold hover:from-[#f0d27a] hover:to-[#e0b84c] transition-all">Try the Live Pacer</button>
              <button onClick={() => setPricingOpen(true)} className="inline-flex items-center justify-center gap-1 px-7 py-3 rounded-xl bg-[#121218] text-white font-semibold border border-[#2a2a33] hover:border-[#c99a2e]/50 transition-all">View Pricing <ChevronRight className="h-4 w-4" /></button>
            </div>
          </div>
        </article>
      </main>

      <Footer />
      <SignInModal open={signInOpen} onClose={() => setSignInOpen(false)} />
      <PricingModal open={pricingOpen} onClose={() => setPricingOpen(false)} />
      <PacerModal open={pacerOpen} onClose={() => setPacerOpen(false)} onPricing={() => { setPacerOpen(false); setPricingOpen(true); }} />
    </>
  );
};

export default BlogPost;
